import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import styled from "styled-components";
import PullToRefresh from "react-simple-pull-to-refresh";
import { Link } from "react-router-dom";
import { useNavigationType } from "react-router";
import { useSelector, useDispatch, batch } from "react-redux";
import {
  fetchSearchInfo,
  fetchPageScroll,
  fetchReset,
  updateScheduleList,
} from "redux/slices/schedule/scheduleSlice";
import { updateCoachList } from "redux/slices/partner/partnerSlice";

import { getToday } from "utils/date";
import iconCalendar from "assets/icons/icon_calendar_blue.svg";

import { MainLayout } from "layouts";
import { Avatar, NotificationIcon } from "components/common";
import Observer from "components/ui/Observer";
import PopupBranch from "components/ui/popup/Branch";
import PullToRefreshing from "components/common/PullToRefreshing";

import ScheduleList from "./ScheduleList";
import ScheduleFilter from "./ScheduleFilter";
import InputSwitch from "components/ui/input/Switch";

/**
 * [스케줄 인덱스]
 */

export const Schedule = () => {
  const mainRef = useRef();
  const topRef = useRef();
  const bottomRef = useRef();
  const dispatch = useDispatch();
  const navigationType = useNavigationType();

  const {
    searchPartner,
    searchBranch,
    searchDate,
    searchCancelYn,
    scheduleList,
  } = useSelector((state) => state.schedule);
  const { userInfo } = useSelector((state) => state.user);
  const { seqPartner, seqPartnerBranch, branchUseYn } = useSelector(
    (state) => state.partner
  );
  const [isPullToRefresh, setIsPullToRefresh] = useState(true);

  useEffect(() => {
    const resizeFilter = () => {
      if (topRef.current && bottomRef.current) {
        const height = topRef.current.offsetHeight;
        topRef.current.style.height = height + "px";
        bottomRef.current.style.top = height + 60 + "px";
      }
    };
    resizeFilter();
  }, []);

  useEffect(() => {
    if (!searchDate) {
      dispatch(
        fetchSearchInfo({
          searchDate: getToday(),
        })
      );
    }
  }, [searchDate]);

  useEffect(() => {
    if (searchPartner != seqPartner || searchBranch != seqPartnerBranch) {
      batch(() => {
        dispatch(
          fetchSearchInfo({
            searchPartner: seqPartner,
            searchBranch: seqPartnerBranch,
            searchBranchUseYn: branchUseYn,
            searchCoach: 0,
          })
        );
        dispatch(updateCoachList());
      });
    }
  }, [seqPartner, seqPartnerBranch]);

  useLayoutEffect(() => {
    if (navigationType != "POP") {
      batch(() => {
        dispatch(
          fetchReset({
            searchPartner: seqPartner,
            searchBranch: seqPartnerBranch,
            searchDate: getToday(),
          })
        );
        dispatch(updateCoachList());
      });
    }
    return () => {
      dispatch(fetchPageScroll("store"));
    };
  }, []);

  const eventHandler = {
    changeQuickButton: (value) => {
      const parent = mainRef.current;
      const child = topRef.current;
      if (!(parent && child)) return;

      switch (value) {
        case "hidden":
          parent.classList.add("focus");
          parent.classList.add("none");
          setTimeout(() => {
            parent.classList.remove("none");
          }, 100);
          break;

        case "visible":
          parent.classList.remove("focus");
          child.classList.remove("focus");
          break;

        case "toggle":
          child.classList.toggle("focus");
          break;

        case "check":
          if (parent.classList.contains("focus")) {
            child.classList.remove("focus");
          }
          break;
      }
    },
    pullToRefresh: () => {
      return new Promise((resolve) => {
        setTimeout(() => {
          dispatch(updateScheduleList(false));
          dispatch(updateCoachList());
          resolve();
        }, 700);
      });
    },
    setPullToRefresh: (event) => {
      if (mainRef.current && mainRef.current.classList.contains("focus"))
        return;
      switch (event.type) {
        case "touchstart":
          setIsPullToRefresh(false);
          break;

        case "touchend":
        case "touchcancel":
          setIsPullToRefresh(true);
          break;
      }
    },
    changeToday: () => {
      const summaryInfo = ((scheduleList || {}).data || {}).summaryInfo || {};
      const today = getToday();
      if (summaryInfo[today]) {
        eventHandler.setScrollToDate(today);
      } else {
        dispatch(
          fetchSearchInfo({
            searchDate: today,
          })
        );
      }
    },
    changeCancelYn: (event) => {
      const isCheck = event.currentTarget.checked;
      const relativeDate = eventHandler.getDateByScroll();
      dispatch(
        fetchSearchInfo({
          searchCancelYn: isCheck ? "Y" : "N",
          searchRelativeDate: relativeDate,
        })
      );
      /*
			dispatch(updateScheduleList(true, true));
			setTimeout(() => {
				console.log("relativeDate : " + relativeDate);
				eventHandler.setScrollToDate(relativeDate);
			}, 500);
			*/
    },
    getDateByScroll: () => {
      const container = document.getElementById("content");
      const li = bottomRef.current.querySelectorAll(".dateInfo");
      const scrollTop = container.scrollTop;
      for (let i = 0; i < li.length; i++) {
        li[i].style.position = "relative";
        const top = li[i].offsetTop + 65;
        li[i].style.position = "sticky";
        if (top > scrollTop && li[i - 1]) {
          return li[i - 1].getAttribute("data-date");
        }
      }
      return;
    },
    setScrollToDate: (date) => {
      const container = document.getElementById("content");
      const li = container.querySelector("li[data-date='" + date + "']");
      if (li) {
        li.style.position = "relative";
        const top = li.offsetTop + 65; // 왜 65인지 모르겠다.
        li.style.position = "sticky";
        container.scrollTo({
          top: top,
          behavior: "smooth",
        });
      }
    },
  };

  const render = () => {
    return (
      <PullToRefresh
        onRefresh={eventHandler.pullToRefresh}
        resistance={2}
        pullingContent={<></>}
        refreshingContent={<PullToRefreshing />}
        isPullable={isPullToRefresh}
      >
        <MainLayout
          withHeader
          withBottomTab
          backgroundColor="var(--lightBlue)"
          headerProps={{
            withBackButton: false,
            padding: "14px 24px 14px 24px",
            leftComponent: () => {
              return <PopupBranch isAll={true} />;
            },
            rightComponent: () => (
              <RightWrap>
                <NotificationIcon />
                <Link to="/all">
                  <Avatar src={userInfo.imageUrl} size={32} minSize={false} />
                </Link>
              </RightWrap>
            ),
          }}
          onScroll={() => {
            eventHandler.changeQuickButton("check");
          }}
          withPartner={true}
        >
          <Wrapper ref={mainRef}>
            <div ref={topRef} className="top">
              <ScheduleFilter onTouchEvents={eventHandler.setPullToRefresh} />
            </div>
            <div ref={bottomRef} className="bottom">
              <div className="filter">
                <div>
                  취소일정 보기
                  <InputSwitch
                    size="small"
                    checked={searchCancelYn == "Y"}
                    onChange={eventHandler.changeCancelYn}
                  />
                </div>
                <div>
                  <button onClick={eventHandler.changeToday}>오늘</button>
                </div>
              </div>
              <Observer
                isActive={true}
                onVisible={() => {
                  eventHandler.changeQuickButton("visible");
                }}
                onHidden={() => {
                  eventHandler.changeQuickButton("hidden");
                }}
              />
              <ScheduleList />
              <div
                className="dummy"
                onClick={() => {
                  eventHandler.changeQuickButton("check");
                }}
              />
            </div>
            <button
              onClick={() => {
                eventHandler.changeQuickButton("toggle");
              }}
            />
          </Wrapper>
        </MainLayout>
      </PullToRefresh>
    );
  };

  return render();
};

const Wrapper = styled.div`
  & > button {
    position: fixed;
    right: 15px;
    bottom: 75px;
    width: 56px;
    height: 56px;
    background: white url(${iconCalendar}) no-repeat center center / 24px;
    border-radius: 100%;
    border: 1px solid var(--border);
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.1);
    visibility: hidden;
    opacity: 0;
    transition:
      visibility 0.4s,
      opacity 0.4s;
    z-index: 2;
  }

  &.focus > button {
    visibility: visible;
    opacity: 1;
  }

  & > div {
    position: absolute;
    left: 0;
    top: 60px;
    right: 0;
  }

  & > .top {
    height: 168px;
    border-bottom: 1px solid transparent;
    overflow: hidden;
  }
  &.focus > .top {
    position: fixed;
    left: 0;
    top: 60px;
    right: 0;
    border-color: var(--border);
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.1);
    padding-bottom: 10px;
    box-sizing: initial;
    visibility: hidden;
    opacity: 0;
    transform: translateY(-100%);
    transition:
      visibility 0.4s,
      opacity 0.4s,
      transform 0.4s;
    z-index: 4;
  }
  &.focus > .top.focus {
    transform: translateY(0);
    visibility: visible;
    opacity: 1;
  }
  &.none > .top {
    display: none;
  }

  & > .bottom {
    top: 242px;
    padding-bottom: 60px;
  }
  & > .bottom .dummy {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0; /* background-color:rgba(0,0,0,0.25); */
    visibility: hidden;
    opacity: 0;
    z-index: 2;
    transition:
      visibility 0.4s,
      opacity 0.4s;
  }
  & > .top.focus + .bottom .dummy {
    visibility: visible;
    opacity: 1;
  }

  .bottom .filter {
    display: flex;
    margin-top: -1px;
    padding: 0 24px;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    position: -webkit-sticky;
    top: 60px;
    height: 42px;
    background-color: white;
    font-size: 14px;
    font-weight: 500;
    color: #6b748b;
    z-index: 2;
  }
  .bottom .filter div {
    display: inline-flex;
    align-items: center;
  }
  .bottom .filter label {
    margin-left: 6px;
  }
  .bottom .filter button {
    padding: 3px 12px;
    background-color: white;
    border-radius: 24px;
    border: 1px solid #d9dee8b2;
    font-size: var(--12px);
    color: #4c5874;
  }
`;

const RightWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 18px;
`;

export default Schedule;
